/**
 * Pulpdent Interactive Sales Map JavaScript
 *
 * Handles map interactivity, hover modals, and AJAX rep lookups.
 */

(function ($) {
  'use strict';

  /**
   * Shared utility for creating rep cards
   * Single source of truth for card HTML structure
   */
  const PulpdentUtils = {
    /**
     * Decode HTML entities in a string
     * @param {string} text - The text containing HTML entities
     * @returns {string} The decoded text
     */
    decodeHtmlEntities: function (text) {
      if (!text) return text;
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    },

    /**
     * Return true if the rep is international (not US or Canada)
     * @param {Object} rep - The rep data object
     * @returns {boolean}
     */
    isInternational: function (rep) {
      const countries = Array.isArray(rep.country)
        ? rep.country
        : rep.country
          ? [rep.country]
          : [];
      return countries.some(
        (c) => c !== 'United States' && c !== 'Canada'
      );
    },

    /**
     * Return a comma-joined display string of all assigned countries
     * @param {Object} rep - The rep data object
     * @returns {string}
     */
    getCountryDisplay: function (rep) {
      const countries = Array.isArray(rep.country)
        ? rep.country
        : rep.country
          ? [rep.country]
          : [];
      return countries.join(', ');
    },

    /**
     * Build formatted address string from rep data
     * @param {Object} rep - The rep data object
     * @returns {string} Formatted address
     */
    buildAddress: function (rep) {
      if (!rep.city) return '';
      const parts = [];
      if (rep.address_1) parts.push(rep.address_1);
      if (rep.address_2) parts.push(rep.address_2);
      if (rep.address_3) parts.push(rep.address_3);
      parts.push(rep.city);
      if (rep.state) parts.push(rep.state);
      if (rep.zip_code) parts.push(rep.zip_code);
      const countryVal = Array.isArray(rep.country)
        ? rep.country.join(', ')
        : rep.country;
      const displayCountry = rep.address_country || countryVal;
      const isIntl =
        displayCountry &&
        displayCountry !== 'United States' &&
        displayCountry !== 'Canada';
      if (isIntl) parts.push(displayCountry);
      return parts.join(', ');
    },

    /**
     * Create a rep card element
     * @param {Object} rep - The rep data object
     * @returns {jQuery} The card element
     */
    createRepCard: function (rep) {
      const template = document.getElementById('pulpdent-rep-card-template');

      if (template) {
        const $card = $(template.content.cloneNode(true));
        const isIntl = this.isInternational(rep);
        const countryDisplay = this.getCountryDisplay(rep);

        // Set data-intl-country for the country filter
        $card
          .find('.pulpdent-rep-card')
          .attr(
            'data-intl-country',
            JSON.stringify(
              Array.isArray(rep.country)
                ? rep.country
                : rep.country
                  ? [rep.country]
                  : []
            )
          );

        // Photo — hide entire section if no photo
        if (rep.photo) {
          $card
            .find('.rep-photo-img')
            .attr('src', rep.photo)
            .attr('alt', rep.name);
        } else {
          $card.find('.pulpdent-rep-photo').hide();
        }

        // Title
        if (rep.title) {
          $card.find('.pulpdent-rep-title').text(rep.title);
        } else {
          $card.find('.pulpdent-rep-title').hide();
        }

        // Name
        const decodedName = this.decodeHtmlEntities(rep.name);
        $card.find('.pulpdent-rep-name').text(decodedName);

        // Territory: international reps show country names; others show territory
        const territoryText =
          isIntl && countryDisplay ? countryDisplay : rep.territory || '';
        $card.find('.pulpdent-rep-territory').text(territoryText);

        // Address
        const address = this.buildAddress(rep);
        if (address) {
          $card.find('.pulpdent-rep-address').text(address);
        } else {
          $card.find('.pulpdent-rep-address').hide();
        }

        // Phone — prepend + for international numbers
        if (rep.phone) {
          const phone =
            isIntl && rep.phone[0] !== '+' ? '+' + rep.phone : rep.phone;
          $card
            .find('.pulpdent-rep-phone a')
            .attr('href', 'tel:' + phone)
            .text(phone);
        } else {
          $card.find('.pulpdent-rep-phone').hide();
        }

        // Email or Form Link
        if (rep.form_link) {
          $card
            .find('.pulpdent-rep-email-link')
            .attr('href', rep.form_link)
            .attr('target', '_blank')
            .attr('rel', 'noopener noreferrer')
            .attr('title', 'Contact Form')
            .addClass('pulpdent-rep-form-link');
        } else if (rep.email) {
          $card
            .find('.pulpdent-rep-email-link')
            .attr('href', 'mailto:' + rep.email);
        } else {
          $card.find('.pulpdent-rep-email-link').hide();
        }

        // Website link
        if (rep.website) {
          $card
            .find('.pulpdent-rep-website-link')
            .attr('href', rep.website)
            .show();
        }

        // Sales contact
        if (rep.sales_contact) {
          $card
            .find('.pulpdent-rep-sales-contact')
            .text('Sales contact: ' + rep.sales_contact)
            .show();
        }

        return $card;
      }

      // Fallback: build HTML manually if template doesn't exist
      const isIntlFallback = this.isInternational(rep);
      const countryDisplayFallback = this.getCountryDisplay(rep);
      const decodedName = this.decodeHtmlEntities(rep.name);
      const decodedTitle = this.decodeHtmlEntities(rep.title);
      const addressStr = this.buildAddress(rep);
      const fallbackCountries = Array.isArray(rep.country)
        ? rep.country
        : rep.country
          ? [rep.country]
          : [];
      const intlCountryAttr = JSON.stringify(fallbackCountries).replace(
        /"/g,
        '&quot;'
      );
      const territoryFallback =
        isIntlFallback && countryDisplayFallback
          ? countryDisplayFallback
          : rep.territory || '';
      const phoneFallback =
        rep.phone && isIntlFallback && rep.phone[0] !== '+'
          ? '+' + rep.phone
          : rep.phone;

      let photoHtml = rep.photo
        ? '<div class="pulpdent-rep-photo"><img src="' +
          rep.photo +
          '" alt="' +
          decodedName +
          '" class="rep-photo-img" /></div>'
        : '';

      let titleHtml = decodedTitle
        ? '<p class="pulpdent-rep-title">' + decodedTitle + '</p>'
        : '';

      let addressHtml = addressStr
        ? '<p class="pulpdent-rep-address">' + addressStr + '</p>'
        : '';

      let phoneHtml = phoneFallback
        ? '<p class="pulpdent-rep-phone"><a href="tel:' +
          phoneFallback +
          '">' +
          phoneFallback +
          '</a></p>'
        : '';

      let emailHtml = '';
      if (rep.form_link) {
        emailHtml =
          '<a href="' +
          rep.form_link +
          '" class="pulpdent-rep-email-link pulpdent-rep-form-link" title="Contact Form" target="_blank" rel="noopener noreferrer"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg></a>';
      } else if (rep.email) {
        emailHtml =
          '<a href="mailto:' +
          rep.email +
          '" class="pulpdent-rep-email-link" title="Send Email"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg></a>';
      }

      let websiteHtml = '';
      if (rep.website) {
        websiteHtml =
          '<a href="' +
          rep.website +
          '" class="pulpdent-rep-website-link" title="Visit Website" target="_blank" rel="noopener noreferrer"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15,3 21,3 21,9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg></a>';
      }

      let salesContactHtml = '';
      if (rep.sales_contact) {
        salesContactHtml =
          '<p class="pulpdent-rep-sales-contact">Sales contact: ' +
          rep.sales_contact +
          '</p>';
      }

      return $(
        '<div class="pulpdent-rep-card" data-intl-country="' +
          intlCountryAttr +
          '">' +
          photoHtml +
          titleHtml +
          '<h3 class="pulpdent-rep-name">' +
          decodedName.toUpperCase() +
          '</h3>' +
          '<div class="pulpdent-rep-details">' +
          '<p class="pulpdent-rep-territory">' +
          territoryFallback +
          '</p>' +
          addressHtml +
          phoneHtml +
          '</div>' +
          salesContactHtml +
          '<div class="pulpdent-rep-actions">' +
          emailHtml +
          websiteHtml +
          '</div>' +
          '</div>'
      );
    },
  };

  // Main map controller
  const PulpdentMap = {
    modal: null,
    tooltip: null,
    currentRegion: null,
    hideTimeout: null,
    isModalHovered: false,

    /**
     * Initialize the map
     */
    init: function () {
      this.modal = $('#pulpdent-rep-modal');
      this.bindEvents();
      this.createTooltip();
      this.makeRegionsAccessible();
    },

    /**
     * Make map regions keyboard accessible
     */
    makeRegionsAccessible: function () {
      // Add tabindex and role to all region groups in the main map
      $('.pulpdent-map-main .region-group').each(function () {
        const $region = $(this);
        const regionName = $region.data('region-name');

        $region.attr({
          tabindex: '0',
          role: 'button',
          'aria-label': 'View representatives for ' + regionName,
        });
      });
    },

    /**
     * Create the region tooltip element
     */
    createTooltip: function () {
      this.tooltip = $('<div class="pulpdent-region-tooltip"></div>');
      $('body').append(this.tooltip);
      this.tooltip.hide();
    },

    /**
     * Bind all event handlers
     */
    bindEvents: function () {
      const self = this;

      // Region hover events - only for regions in the main map area
      $(document).on(
        'mouseenter',
        '.pulpdent-map-main .region-group',
        function (e) {
          self.handleRegionEnter($(this), e);
        }
      );

      $(document).on(
        'mouseleave',
        '.pulpdent-map-main .region-group',
        function () {
          self.handleRegionLeave();
        }
      );

      $(document).on(
        'mousemove',
        '.pulpdent-map-main .region-group',
        function (e) {
          self.updateTooltipPosition(e);
        }
      );

      // Modal hover events (keep modal open when hovering over it)
      this.modal.on('mouseenter', function () {
        self.isModalHovered = true;
        self.clearHideTimeout();
      });

      this.modal.on('mouseleave', function () {
        self.isModalHovered = false;
        self.hideModal();
      });

      // Close modal on clicking outside
      $(document).on('click', function (e) {
        if (
          !$(e.target).closest(
            '.pulpdent-map-main .region-group, .pulpdent-rep-modal'
          ).length
        ) {
          self.hideModal(true);
        }
      });

      // Thumbnail clicks for Canada/International
      $(document).on('click', '.pulpdent-map-thumb', function () {
        self.handleThumbnailClick($(this));
      });

      // Keyboard support for thumbnails (Enter/Space)
      $(document).on('keydown', '.pulpdent-map-thumb', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          self.handleThumbnailClick($(this));
        }
      });

      // Keyboard support for regions (Enter/Space to show modal)
      $(document).on(
        'keydown',
        '.pulpdent-map-main .region-group',
        function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const $region = $(this);
            const regionSlug = $region.data('region');
            const regionName = $region.data('region-name');

            // Get reps for this region
            const reps = self.getRepsForRegion(regionSlug);
            if (reps && reps.length > 0) {
              self.showModal(reps, e);
              self.announceToScreenReader(
                'Showing ' +
                  reps.length +
                  ' representative' +
                  (reps.length !== 1 ? 's' : '') +
                  ' for ' +
                  regionName
              );
            }
          }
        }
      );

      // ESC key to close modal
      $(document).on('keyup', function (e) {
        if (e.key === 'Escape') {
          self.hideModal(true);
        }
      });

      // International dealer link — scroll to and open the intl accordion
      $(document).on('click', '.pulpdent-intl-dealer-link', function (e) {
        e.preventDefault();
        self.hideModal(true);
        self.scrollToIntlAccordion();
      });
    },

    /**
     * Scroll to the International Sales accordion and open it
     */
    scrollToIntlAccordion: function () {
      const $intlHeader = $('#accordion-header-intl');
      if ($intlHeader.length === 0) return;

      const siteHeaderHeight = 120;
      const $item = $intlHeader.closest('.pulpdent-accordion-item');
      const $content = $intlHeader
        .closest('h3')
        .siblings('.pulpdent-accordion-content');

      // Close all other accordions first
      $('.pulpdent-accordion-item').removeClass('is-open');
      $('.pulpdent-accordion-header').attr('aria-expanded', 'false');
      $('.pulpdent-accordion-content').attr('hidden', '');
      $('.pulpdent-accordion-content')
        .find('a, button, input, select, textarea')
        .attr('tabindex', '-1');

      // Smooth scroll to the accordion header
      $('html, body')
        .stop(true, false)
        .animate(
          { scrollTop: $intlHeader.offset().top - siteHeaderHeight },
          500
        );

      // Open the accordion after scroll completes
      setTimeout(function () {
        $item.addClass('is-open');
        $intlHeader.attr('aria-expanded', 'true');
        $content.removeAttr('hidden');
        $content
          .find('a, button, input, select, textarea')
          .removeAttr('tabindex');
      }, 520);
    },

    /**
     * Handle mouse entering a region
     */
    handleRegionEnter: function ($region, e) {
      const self = this;
      const regionSlug = $region.data('region');
      const regionName = $region.data('region-name');

      // Clear any pending hide
      this.clearHideTimeout();

      // Add active class
      $('.region-group').removeClass('active');
      $region.addClass('active');

      // Show tooltip
      this.showTooltip(regionName, e);

      // If same region, just show the modal
      if (this.currentRegion === regionSlug && this.modal.is(':visible')) {
        return;
      }

      this.currentRegion = regionSlug;

      // Get reps for this region (instant from preloaded data)
      const reps = this.getRepsForRegion(regionSlug);
      if (reps && reps.length > 0) {
        this.showModal(reps, e);
      }
    },

    /**
     * Get reps for a region from preloaded data (instant)
     */
    getRepsForRegion: function (regionSlug) {
      if (
        typeof pulpdentMapData !== 'undefined' &&
        pulpdentMapData.repsByRegion &&
        pulpdentMapData.repsByRegion[regionSlug]
      ) {
        return pulpdentMapData.repsByRegion[regionSlug];
      }
      return [];
    },

    /**
     * Handle mouse leaving a region
     */
    handleRegionLeave: function () {
      const self = this;

      // Hide tooltip
      this.hideTooltip();

      // Remove active class with delay
      this.hideTimeout = setTimeout(function () {
        if (!self.isModalHovered) {
          $('.region-group').removeClass('active');
          self.hideModal();
        }
      }, 300);
    },

    /**
     * Clear the hide timeout
     */
    clearHideTimeout: function () {
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }
    },

    /**
     * Show the tooltip
     */
    showTooltip: function (text, e) {
      this.tooltip.text(text).show();
      this.updateTooltipPosition(e);
    },

    /**
     * Update tooltip position
     */
    updateTooltipPosition: function (e) {
      if (this.tooltip.is(':visible')) {
        this.tooltip.css({
          left: e.pageX,
          top: e.pageY - 15,
        });
      }
    },

    /**
     * Hide the tooltip
     */
    hideTooltip: function () {
      this.tooltip.hide();
    },

    /**
     * Check if the international map is currently displayed in the main area
     */
    isInternationalMapActive: function () {
      const $mapLabel = $('.pulpdent-map-label');
      return $mapLabel.text().trim().toUpperCase() === 'INTERNATIONAL';
    },

    /**
     * Show the modal with rep cards
     */
    showModal: function (reps, e) {
      const self = this;
      const $grid = $('#pulpdent-rep-grid');

      // Clear existing content
      $grid.empty();

      // For international map, show a link to the international accordion instead
      if (this.isInternationalMapActive()) {
        this.modal
          .removeClass('single-rep two-reps multi-reps')
          .addClass('single-rep');
        $grid.html(
          '<div class="pulpdent-intl-dealer-message" role="status">' +
            '<p><a href="#accordion-header-intl" class="pulpdent-intl-dealer-link">Click here</a> ' +
            'to view our international dealers.' +
            '</p></div>'
        );

        this.positionModal(e);
        this.modal.show();
        this.modal.attr('aria-hidden', 'false');
        return;
      }

      if (reps.length === 0) {
        $grid.html(
          '<div class="pulpdent-no-reps" role="status">' +
            pulpdentMapData.strings.noReps +
            '</div>'
        );
      } else {
        // Add class for responsive grid styling based on rep count
        this.modal
          .removeClass('single-rep two-reps multi-reps')
          .addClass(
            reps.length === 1
              ? 'single-rep'
              : reps.length === 2
              ? 'two-reps'
              : 'multi-reps'
          );

        // Create rep cards
        reps.forEach(function (rep, index) {
          const $card = self.createRepCard(rep);
          $card.attr('role', 'listitem');
          $grid.append($card);
        });
      }

      // Position and show modal
      this.positionModal(e);
      this.modal.show();
      this.modal.attr('aria-hidden', 'false');

      // Announce to screen readers
      if (reps.length > 0) {
        this.announceToScreenReader(
          'Showing ' +
            reps.length +
            ' sales representative' +
            (reps.length !== 1 ? 's' : '')
        );
      }
    },

    /**
     * Create a rep card element (uses shared utility)
     */
    createRepCard: function (rep) {
      return PulpdentUtils.createRepCard(rep);
    },

    /**
     * Position the modal near the mouse
     */
    positionModal: function (e) {
      const windowWidth = $(window).width();
      const windowHeight = $(window).height();

      // Get modal dimensions (temporarily show to measure)
      this.modal.css({ visibility: 'hidden', display: 'block' });
      const modalWidth = this.modal.outerWidth();
      const modalHeight = this.modal.outerHeight();
      this.modal.css({ visibility: 'visible' });

      // Use clientX/clientY for fixed positioning (viewport-relative)
      let left = e.clientX + 20;
      let top = e.clientY + 20;

      // Adjust if modal goes off screen right
      if (left + modalWidth > windowWidth - 20) {
        left = e.clientX - modalWidth - 20;
      }

      // Adjust if modal goes off screen bottom
      if (top + modalHeight > windowHeight - 20) {
        top = e.clientY - modalHeight - 20;
      }

      // Ensure minimum positions (keep within viewport)
      left = Math.max(20, left);
      top = Math.max(20, top);

      // Ensure modal doesn't exceed viewport
      left = Math.min(left, windowWidth - modalWidth - 20);
      top = Math.min(top, windowHeight - modalHeight - 20);

      this.modal.css({
        left: left,
        top: top,
      });
    },

    /**
     * Hide the modal
     */
    hideModal: function (immediate) {
      if (immediate) {
        this.modal.hide();
        this.modal.attr('aria-hidden', 'true');
        this.currentRegion = null;
        $('.region-group').removeClass('active');
      } else if (!this.isModalHovered) {
        this.modal.hide();
        this.modal.attr('aria-hidden', 'true');
        this.currentRegion = null;
      }
    },

    /**
     * Announce message to screen readers
     */
    announceToScreenReader: function (message) {
      const $announcer = $('#map-announcer');
      if ($announcer.length > 0) {
        $announcer.text(message);
        // Clear after a delay to allow for re-announcements
        setTimeout(function () {
          $announcer.text('');
        }, 1000);
      }
    },

    /**
     * Handle thumbnail clicks (Canada/International)
     * Swaps the clicked thumbnail map with the main map
     */
    handleThumbnailClick: function ($clickedThumb) {
      const $mainMapContainer = $('#pulpdent-us-map');
      const $mapLabel = $('.pulpdent-map-label');
      const mapType = $clickedThumb.data('map');
      const $thumbImgContainer = $clickedThumb.find('.pulpdent-map-thumb-img');

      // Get the current main map SVG
      let $mainSvg = $mainMapContainer.find('svg').first();
      if ($mainSvg.length === 0) {
        $mainSvg = $mainMapContainer.children('svg').first();
      }

      // Get the current main map label text
      const currentMainLabel = $mapLabel.text().trim();

      // Get the current thumbnail label text
      const $clickedThumbLabel = $clickedThumb.find(
        '.pulpdent-map-thumb-label'
      );
      const currentThumbLabel = $clickedThumbLabel.text().trim();

      // Get the SVG element from the thumbnail
      let $thumbSvg = $thumbImgContainer.find('svg').first();

      // If no SVG found, try getting direct children
      if ($thumbSvg.length === 0) {
        $thumbSvg = $thumbImgContainer.children('svg').first();
      }

      // If still no SVG, try getting the HTML content and parsing it
      if ($thumbSvg.length === 0) {
        const thumbContent = $thumbImgContainer.html();
        if (thumbContent && thumbContent.trim().length > 0) {
          // Create a temporary container to parse the HTML
          const $temp = $('<div>').html(thumbContent);
          $thumbSvg = $temp.find('svg').first();

          // If still no SVG, the content might be the SVG itself
          if (
            $thumbSvg.length === 0 &&
            thumbContent.trim().indexOf('<svg') === 0
          ) {
            $thumbSvg = $(thumbContent);
          }
        }
      }

      if ($thumbSvg.length === 0) {
        console.error('Could not find SVG in thumbnail for map type:', mapType);
        return;
      }

      if ($mainSvg.length === 0) {
        console.error('Could not find main map SVG');
        return;
      }

      // Clone both SVGs
      const $clonedThumbSvg = $thumbSvg.clone(true, true);
      const $clonedMainSvg = $mainSvg.clone(true, true);

      // Preserve important SVG attributes for the thumbnail SVG going to main
      if ($thumbSvg.attr('viewBox')) {
        $clonedThumbSvg.attr('viewBox', $thumbSvg.attr('viewBox'));
      }
      if ($thumbSvg.attr('preserveAspectRatio')) {
        $clonedThumbSvg.attr(
          'preserveAspectRatio',
          $thumbSvg.attr('preserveAspectRatio')
        );
      }
      if ($thumbSvg.attr('class')) {
        $clonedThumbSvg.attr('class', $thumbSvg.attr('class'));
      }

      // Preserve important SVG attributes for the main SVG going to thumbnail
      if ($mainSvg.attr('viewBox')) {
        $clonedMainSvg.attr('viewBox', $mainSvg.attr('viewBox'));
      }
      if ($mainSvg.attr('preserveAspectRatio')) {
        $clonedMainSvg.attr(
          'preserveAspectRatio',
          $mainSvg.attr('preserveAspectRatio')
        );
      }
      if ($mainSvg.attr('class')) {
        $clonedMainSvg.attr('class', $mainSvg.attr('class'));
      }

      // Hide any open modal before swapping
      this.hideModal(true);

      // Remove any active states from regions before swapping
      $('.region-group').removeClass('active');

      // Swap: Put thumbnail SVG into main area
      $mainMapContainer.empty().append($clonedThumbSvg);

      // Swap: Put main SVG into thumbnail
      $thumbImgContainer.empty().append($clonedMainSvg);

      // Remove any active states from the SVG now in the thumbnail
      $thumbImgContainer.find('.region-group').removeClass('active');

      // Remove accessibility attributes from thumbnail regions
      $thumbImgContainer
        .find('.region-group')
        .removeAttr('tabindex role aria-label');

      // Add accessibility attributes to new main map regions
      this.makeRegionsAccessible();

      // Swap the labels: thumbnail label goes to main, main label goes to thumbnail
      $mapLabel.text(currentThumbLabel);

      if ($clickedThumbLabel.length > 0) {
        $clickedThumbLabel.text(currentMainLabel);
      }

      // Update the data-map attribute of the clicked thumbnail to reflect its new content
      const newMapType = this.getMapTypeFromLabel(currentMainLabel);
      $clickedThumb.attr('data-map', newMapType);

      // Update aria-label for the clicked thumbnail
      $clickedThumb.attr(
        'aria-label',
        'Switch to ' + currentMainLabel + ' map'
      );

      // Update active state on thumbnails
      $('.pulpdent-map-thumb').removeClass('active');
      $clickedThumb.addClass('active');

      // Announce map change to screen readers
      this.announceToScreenReader('Now showing ' + currentThumbLabel + ' map');

      // Reset current region tracking
      this.currentRegion = null;
    },

    /**
     * Helper function to get map type from label text
     */
    getMapTypeFromLabel: function (label) {
      const labelUpper = label.toUpperCase().trim();
      if (labelUpper === 'CANADA') {
        return 'canada';
      } else if (labelUpper === 'INTERNATIONAL') {
        return 'international';
      } else if (labelUpper === 'UNITED STATES') {
        return 'united-states';
      }
      return 'united-states'; // default
    },
  };

  // Filter Section Controller
  const PulpdentFilter = {
    searchInput: null,
    searchResults: null,

    /**
     * Initialize the filter section
     */
    init: function () {
      this.searchInput = $('#pulpdent-search-input');
      this.searchResults = $('#pulpdent-search-results');

      this.setAccordionTabIndexes();
      this.bindEvents();
    },

    /**
     * Bind all event handlers
     */
    bindEvents: function () {
      const self = this;

      // Search button click
      $(document).on('click', '.pulpdent-search-btn', function () {
        self.performSearch();
      });

      // Search on Enter key
      this.searchInput.on('keypress', function (e) {
        if (e.which === 13) {
          e.preventDefault();
          self.performSearch();
        }
      });

      // Clear results button
      $('#pulpdent-clear-results').on('click', function () {
        self.clearSearchResults();
      });

      // Accordion headers - click
      $(document).on('click', '.pulpdent-accordion-header', function () {
        self.toggleAccordion($(this));
      });

      // Accordion headers - keyboard navigation
      $(document).on('keydown', '.pulpdent-accordion-header', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          self.toggleAccordion($(this));
        }
      });

      // International country filter — toggle button click
      $(document).on('click', '#pulpdent-intl-country-toggle', function (e) {
        e.stopPropagation();
        self.toggleIntlCountryDropdown();
      });

      // International country filter — toggle button keyboard
      $(document).on('keydown', '#pulpdent-intl-country-toggle', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          self.toggleIntlCountryDropdown();
        } else if (e.key === 'Escape') {
          self.closeIntlCountryDropdown();
          $(this).focus();
        }
      });

      // International country filter — radio selection
      $(document).on(
        'change',
        'input[name="pulpdent-intl-country"]',
        function () {
          var selected = $(this).val();
          var labelText = selected || 'All Countries';
          $(
            '#pulpdent-intl-country-toggle .pulpdent-intl-country-toggle-label'
          ).text(labelText);
          self.closeIntlCountryDropdown();
          self.filterIntlByCountry(selected);
        }
      );

      // Close intl country dropdown when clicking outside
      $(document).on('click', function (e) {
        if (
          !$(e.target).closest('.pulpdent-intl-country-filter-wrapper').length
        ) {
          self.closeIntlCountryDropdown();
        }
      });

      // Close intl country dropdown on ESC
      $(document).on('keydown', function (e) {
        if (
          e.key === 'Escape' &&
          $('#pulpdent-intl-country-list').hasClass('is-open')
        ) {
          self.closeIntlCountryDropdown();
          $('#pulpdent-intl-country-toggle').focus();
        }
      });
    },

    /**
     * Perform search
     */
    performSearch: function () {
      const query = this.searchInput.val().trim();

      if (query.length < 2) {
        return;
      }

      this.fetchSearchResults(query);
    },

    /**
     * Fetch search results via AJAX
     */
    fetchSearchResults: function (query) {
      const self = this;

      if (typeof pulpdentMapData === 'undefined') {
        console.error('pulpdentMapData is not defined');
        return;
      }

      this.showResultsLoading();

      $.ajax({
        url: pulpdentMapData.ajaxUrl,
        type: 'POST',
        data: {
          action: 'pulpdent_search_reps',
          search: query,
          nonce: pulpdentMapData.nonce,
        },
        success: function (response) {
          if (response.success && response.data && response.data.length > 0) {
            self.displaySearchResults(response.data);
          } else {
            self.displayNoResults();
          }
        },
        error: function () {
          console.error('Error searching reps');
          self.displayNoResults();
        },
      });
    },

    /**
     * Show loading state in results
     */
    showResultsLoading: function () {
      this.searchResults.show();
      $('#pulpdent-results-grid').html(
        '<div class="pulpdent-accordion-loading"><div class="pulpdent-spinner"></div></div>'
      );
    },

    /**
     * Display search results
     */
    displaySearchResults: function (reps) {
      const self = this;
      const $grid = $('#pulpdent-results-grid');

      $grid.empty();
      this.searchResults.show();

      if (reps.length === 0) {
        this.displayNoResults();
        return;
      }

      reps.forEach(function (rep) {
        const $card = self.createRepCard(rep);
        $grid.append($card);
      });

      // Announce results for screen readers
      this.announceForScreenReader(
        'Found ' +
          reps.length +
          ' sales representative' +
          (reps.length !== 1 ? 's' : '')
      );
    },

    /**
     * Display no results message
     */
    displayNoResults: function () {
      this.searchResults.show();
      $('#pulpdent-results-grid').html(
        '<div class="pulpdent-no-results-message">No sales representatives found for your search.</div>'
      );

      // Announce for screen readers
      this.announceForScreenReader(
        'No sales representatives found for your search'
      );
    },

    /**
     * Clear search results
     */
    clearSearchResults: function () {
      this.searchInput.val('');
      $('#pulpdent-results-grid').empty();
      this.searchResults.hide();
    },

    /**
     * Toggle accordion section
     */
    toggleAccordion: function ($header) {
      const $item = $header.closest('.pulpdent-accordion-item');
      const $content = $header.siblings('.pulpdent-accordion-content');
      const isOpen = $item.hasClass('is-open');
      const siteHeaderHeight = 120; // Account for fixed site header

      if (isOpen) {
        // Just close it
        $item.removeClass('is-open');
        $header.attr('aria-expanded', 'false');
        $content.attr('hidden', '');
        // Make focusable elements non-tabbable
        $content
          .find('a, button, input, select, textarea')
          .attr('tabindex', '-1');
        // Reset intl filter if this is the international accordion
        if ($header.attr('id') === 'accordion-header-intl') {
          this.resetIntlFilter();
        }
        return;
      }

      // Check if there's a currently open accordion ABOVE the clicked one
      const $currentlyOpen = $('.pulpdent-accordion-item.is-open');
      const hasOpenAccordionAbove =
        $currentlyOpen.length > 0 && $currentlyOpen.index() < $item.index();

      // Close all accordions first
      $('.pulpdent-accordion-item').removeClass('is-open');
      $('.pulpdent-accordion-header').attr('aria-expanded', 'false');
      $('.pulpdent-accordion-content').attr('hidden', '');
      // Make all focusable elements in closed accordions non-tabbable
      $('.pulpdent-accordion-content')
        .find('a, button, input, select, textarea')
        .attr('tabindex', '-1');

      if (hasOpenAccordionAbove) {
        // There was an accordion open above - wait for it to close, then scroll and open
        setTimeout(function () {
          const targetScroll = $header.offset().top - siteHeaderHeight;

          $('html, body').stop(true, false).animate(
            {
              scrollTop: targetScroll,
            },
            250
          );

          setTimeout(function () {
            $item.addClass('is-open');
            $header.attr('aria-expanded', 'true');
            $content.removeAttr('hidden');
            // Make focusable elements tabbable again
            $content
              .find('a, button, input, select, textarea')
              .removeAttr('tabindex');
          }, 270);
        }, 200); // Wait for close animation
      } else {
        // No accordion above was open - check if we need to scroll
        const headerTop = $header.offset().top;
        const viewportTop = $(window).scrollTop() + siteHeaderHeight;
        const viewportHeight = $(window).height();
        const comfortZone = viewportTop + viewportHeight * 0.5;
        const isHeaderInGoodPosition =
          headerTop >= viewportTop && headerTop <= comfortZone;

        if (isHeaderInGoodPosition) {
          // Header is in a good spot, just open
          $item.addClass('is-open');
          $header.attr('aria-expanded', 'true');
          $content.removeAttr('hidden');
          // Make focusable elements tabbable again
          $content
            .find('a, button, input, select, textarea')
            .removeAttr('tabindex');
        } else {
          // Need to scroll first
          const targetScroll = headerTop - siteHeaderHeight;

          $('html, body').stop(true, false).animate(
            {
              scrollTop: targetScroll,
            },
            250
          );

          setTimeout(function () {
            $item.addClass('is-open');
            $header.attr('aria-expanded', 'true');
            $content.removeAttr('hidden');
            // Make focusable elements tabbable again
            $content
              .find('a, button, input, select, textarea')
              .removeAttr('tabindex');
          }, 270);
        }
      }
    },

    /**
     * Toggle the international country dropdown
     */
    toggleIntlCountryDropdown: function () {
      if ($('#pulpdent-intl-country-list').hasClass('is-open')) {
        this.closeIntlCountryDropdown();
      } else {
        this.openIntlCountryDropdown();
      }
    },

    /**
     * Open the international country dropdown
     */
    openIntlCountryDropdown: function () {
      $('#pulpdent-intl-country-list').addClass('is-open');
      $('#pulpdent-intl-country-toggle').attr('aria-expanded', 'true');
      // Allow the dropdown panel to overflow the accordion grid's clip
      $('#accordion-content-intl .pulpdent-accordion-grid').addClass(
        'pulpdent-overflow-visible'
      );
    },

    /**
     * Close the international country dropdown
     */
    closeIntlCountryDropdown: function () {
      $('#pulpdent-intl-country-list').removeClass('is-open');
      $('#pulpdent-intl-country-toggle').attr('aria-expanded', 'false');
      $('#accordion-content-intl .pulpdent-accordion-grid').removeClass(
        'pulpdent-overflow-visible'
      );
    },

    /**
     * Filter international reps by country selection
     */
    filterIntlByCountry: function (selected) {
      var $grid = $('#accordion-content-intl .pulpdent-accordion-grid');
      var $cards = $grid.find('.pulpdent-rep-card');
      $grid.find('.pulpdent-intl-no-filter-results').remove();

      if (!selected) {
        $cards.show();
        return;
      }

      var visibleCount = 0;
      $cards.each(function () {
        var cardCountries = $(this).data('intl-country');
        var matches = Array.isArray(cardCountries)
          ? cardCountries.indexOf(selected) !== -1
          : cardCountries === selected;
        if (matches) {
          $(this).show();
          visibleCount++;
        } else {
          $(this).hide();
        }
      });

      if (visibleCount === 0) {
        $grid.append(
          '<div class="pulpdent-intl-no-filter-results pulpdent-no-results-message" role="status">No sales representatives found for the selected country.</div>'
        );
      }

      this.announceForScreenReader(
        visibleCount > 0
          ? 'Showing ' +
              visibleCount +
              ' representative' +
              (visibleCount !== 1 ? 's' : '') +
              ' for ' +
              selected
          : 'No sales representatives found for ' + selected
      );
    },

    /**
     * Reset the international country filter to show all reps
     */
    resetIntlFilter: function () {
      this.closeIntlCountryDropdown();
      $('input[name="pulpdent-intl-country"]').prop('checked', false);
      $('input[name="pulpdent-intl-country"][value=""]').prop('checked', true);
      $(
        '#pulpdent-intl-country-toggle .pulpdent-intl-country-toggle-label'
      ).text('All Countries');
      $(
        '#accordion-content-intl .pulpdent-accordion-grid .pulpdent-rep-card'
      ).show();
      $(
        '#accordion-content-intl .pulpdent-accordion-grid .pulpdent-intl-no-filter-results'
      ).remove();
    },

    /**
     * Create a rep card element (uses shared utility)
     */
    createRepCard: function (rep) {
      return PulpdentUtils.createRepCard(rep);
    },

    /**
     * Announce message for screen readers
     */
    announceForScreenReader: function (message) {
      let $ariaLive = $('#pulpdent-filter-aria-live');
      if ($ariaLive.length === 0) {
        $ariaLive = $(
          '<div id="pulpdent-filter-aria-live" aria-live="polite" class="sr-only"></div>'
        );
        $('body').append($ariaLive);
      }
      $ariaLive.text(message);
    },

    /**
     * Set tabindex on focusable elements in closed accordions
     */
    setAccordionTabIndexes: function () {
      // Find all closed accordion content panels
      $('.pulpdent-accordion-content[hidden]').each(function () {
        // Make all focusable elements non-tabbable
        $(this)
          .find('a, button, input, select, textarea')
          .attr('tabindex', '-1');
      });
    },
  };

  // Initialize when DOM is ready
  $(document).ready(function () {
    // Only initialize if the map wrapper exists
    if ($('.pulpdent-map-wrapper').length) {
      PulpdentMap.init();
    }

    // Initialize filter section if it exists
    if ($('.pulpdent-filter-section').length) {
      PulpdentFilter.init();
    }
  });
})(jQuery);
